import React, { Fragment, useContext, useEffect, useRef } from 'react';
import { object, bool, shape, string, node, func } from 'prop-types';
import clsx from 'clsx';
import {
  useMatchMedia,
  useBreakpoint,
  AccordionPanelStyled,
  AccordionStyled,
  TextStyled,
} from '@nike/nike-design-system-components';
import { LocationPin } from '@nike/nike-design-system-icons';
import useBodega from '../../utils/hooks/bodegaHook';
import useLocale from '../../utils/hooks/localeHook';
import {
  StyledGeo,
  StyledList,
  StyledWrapper,
  StyledLanguageModal,
  StyledLocationPin,
  countryLink,
  accordionPanelWrapper,
  StyledAccordionHeader,
  closeButton,
  languageModalRoot,
  hiddenFocusableAccessibilityButton,
  localeLabelContainer,
} from './styles';
import useGlobalization from '../../utils/hooks/globalizationHook';
import {
  appendQueryParams,
  getCurrentPathname,
  getCurrentQueryParams,
  getLocalizedUrl,
} from '../../utils/url';
import { BODEGA_DEFAULTS, OFF_PLATFORM_OVERRIDE_URL } from '../../constants';
import { useNavOptionsContext } from '../../context/optionsContext';
import { isArabic } from '../../utils/languageTunnelUtils';
import Link from '../Link';
import { AnalyticsContext } from '../../context/analyticsContext';
import { setLocale } from '../../../utils/geo';

/**
 * contains the content of the modal, making our own so we can add invisible buttons
 * at the start and end of the modal. When focus lands on either button that means the user
 * is no longer inside the modal and it should be closed
 * @param {*} params
 * @param {string} params.className
 * @param {node} params.children
 * @param {func} params.handleModalClose
 * @returns {JSX.Element}
 */
const ModalContainer = ({ className, children, handleModalClose, ...rest }) => {
  return (
    <section className={className} {...rest}>
      <button className={hiddenFocusableAccessibilityButton} onFocus={handleModalClose} />
      {children}
      <button onFocus={handleModalClose} />
    </section>
  );
};
ModalContainer.propTypes = {
  className: string,
  children: node,
  handleModalClose: func,
};

const CountryList = ({ geo }) => {
  // Get current locale to extract the pathname from current URL
  const currentLocale = useLocale();
  // Get current query params once for all country links
  const currentQueryParams = getCurrentQueryParams();
  // Get current pathname to preserve when switching countries (e.g., '/experiences/events')
  const currentPathname = getCurrentPathname(
    currentLocale?.cloudUrlFragment,
    currentLocale?.country,
    currentLocale?.urlParam,
  );

  return (
    <StyledList data-testid={`${geo.name}-list`}>
      {/* creates svg during ssr to be later used in the list
       * this allows us to ship 75kbs less html TG-6145
       */}
      <div style={{ display: 'none' }}>
        <LocationPin id="LocationPin" />
      </div>
      {geo?.countries?.map(countryObject => {
        const { countryName, langRegion, languageName, urlParam, country, cloudUrlFragment } =
          countryObject;
        const uppercaseCountry = country.toUpperCase();
        const arabicClassName = isArabic(countryObject) ? 'arabic' : '';
        const overriddenHref = OFF_PLATFORM_OVERRIDE_URL[uppercaseCountry];
        const { analyticsClient } = useContext(AnalyticsContext);
        // Only preserve path for experiences app — other paths may not exist across locales
        const preservedPathname = currentPathname.startsWith('experiences') ? currentPathname : '';
        const baseHref =
          overriddenHref ||
          getLocalizedUrl(preservedPathname)({
            urlParam,
            country,
            cloudUrlFragment,
          });
        const href = appendQueryParams(baseHref, currentQueryParams);
        return (
          <li key={`${countryName}-${languageName}-list`}>
            <Link
              className={clsx(countryLink, arabicClassName)}
              href={href}
              key={`${countryName}-${languageName}-wrapper`}
              onClick={() => {
                setLocale(uppercaseCountry, langRegion.replace('-', '_'), true);
                analyticsClient?.clickNavCountrySelectionSuccess({
                  country: uppercaseCountry,
                  language: urlParam,
                });
              }}
            >
              <StyledLocationPin>
                {/* using the svg defined above */}
                <use href="#LocationPin" />
              </StyledLocationPin>
              <div className={localeLabelContainer}>
                <TextStyled
                  appearance="title4"
                  className={arabicClassName}
                  lang={langRegion?.toLowerCase()}
                >
                  {countryName}
                </TextStyled>
                <TextStyled
                  appearance="body3"
                  className={arabicClassName}
                  color="secondary"
                  lang={langRegion?.toLowerCase()}
                >
                  {languageName}
                </TextStyled>
              </div>
            </Link>
          </li>
        );
      })}
    </StyledList>
  );
};

CountryList.propTypes = {
  geo: object,
};

/**
 * Component to display language menu content - country links
 * @param {bool} displayOnPage - default is false. true if the menu should be displayed on the page
 */
export const LanguageMenuContent = ({ displayOnPage = false }) => {
  const geos = useGlobalization();
  const languageContentClassName = { 'is-hidden': !displayOnPage };
  const { mounted } = useNavOptionsContext() || {};
  const isMobileQuery = useMatchMedia(useBreakpoint('lt', 'm'));
  // Prevent hydration mismatch: assume desktop during SSR and initial render
  const isMobile = mounted ? isMobileQuery : false;
  return (
    <StyledWrapper className={clsx(languageContentClassName)} data-testid="languageContentWrapper">
      {!isMobile ? (
        geos?.map(geo => (
          <Fragment key={geo?.name}>
            <StyledGeo id={geo?.name}>{geo?.name}</StyledGeo>
            <CountryList geo={geo} />
          </Fragment>
        ))
      ) : (
        <Fragment key={geos?.name}>
          <AccordionStyled animatable={true} id={`geo-accordion`} singlePanelOpenAtATime={true}>
            {geos?.map((geo, index) => (
              <AccordionPanelStyled
                classes={{ summary: accordionPanelWrapper }}
                detailsTitle={<StyledAccordionHeader>{geo?.name}</StyledAccordionHeader>}
                id={`accordion-panel-${index}`}
                key={index}
              >
                <CountryList geo={geo} />
              </AccordionPanelStyled>
            ))}
          </AccordionStyled>
        </Fragment>
      )}
    </StyledWrapper>
  );
};

LanguageMenuContent.propTypes = {
  displayOnPage: bool,
};

const LanguageMenu = ({ shouldFocusOpenLangTunnelButton, isOpen, closeModal }) => {
  const closeModalLabel = useBodega('nav_close_modal', BODEGA_DEFAULTS.CLOSE_MODAL);
  const { analyticsClient } = useContext(AnalyticsContext);
  const locale = useLocale();
  const { SELECT_YOUR_LOCATION } = BODEGA_DEFAULTS;
  const modalRef = useRef(null);
  // Updates the context so that tunnel is closed
  const handleModalClose = () => {
    shouldFocusOpenLangTunnelButton.current = true;
    analyticsClient?.clickNavCountrySelectionDismiss({ country: locale?.country });
    closeModal();
  };

  useEffect(() => {
    if (isOpen) {
      /**
       * inside the modal, find the first element with the class close-btn-container
       * find the first button inside of there and focus on it
       */
      modalRef.current
        ?.getElementsByClassName('close-btn-container')?.[0]
        .getElementsByTagName('button')?.[0]
        ?.focus();
    }
  }, [isOpen]);

  return (
    <>
      <StyledLanguageModal
        appearance="fullscreen"
        classes={{
          closeButton,
        }}
        closeModal={handleModalClose}
        components={{ ModalContainerComp: ModalContainer }}
        data-testid="language_modal"
        id="language_modal"
        isOpen={isOpen}
        modalCloseAriaLabel={closeModalLabel}
        modalContainerCompProps={{ handleModalClose, className: languageModalRoot }}
        modalPortalCompProps={{ trapFocus: false }}
        ref={modalRef}
        title={useBodega('nav-geoselection-title', SELECT_YOUR_LOCATION)}
      >
        {/* displayOnPage is set to true so that the country links are displayed on the modal */}
        {<LanguageMenuContent displayOnPage />}
      </StyledLanguageModal>
    </>
  );
};

LanguageMenu.propTypes = {
  shouldFocusOpenLangTunnelButton: shape({ current: bool }),
  isOpen: bool,
  closeModal: func,
};
export default LanguageMenu;
